import { memo, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { format } from 'date-fns';
import { analyticsActions } from '@actions';
import { keywordsService } from '@services/keywords';
import { useTranslation } from '@hooks';
import TopCategoriesCard from './TopCategoriesCard';
import CategoryMatrixTable from './CategoryMatrixTable';
import KeywordCloud from './KeywordCloud';
import KeywordsTableCard from './KeywordsTableCard';
import { InsightsContainer, PairedRow } from './styles';
import { RoutePath } from '@constants/routes';

const OperationalInsights = memo(({ filter, filterVersion }) => {
    const lastFetchedVersion = useRef(-1);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [cloudData, setCloudData] = useState([]);
    const [cloudLoading, setCloudLoading] = useState(false);

    const { operationalInsightsData, operationalInsightsLoading } = useSelector(state => state.analytics);
    const dispatch = useDispatch();

    const fetchCloudData = useCallback(query => {
        setCloudLoading(true);
        keywordsService
            .get(query, 0, 50)
            .then(result => {
                setCloudData(result?.items || []);
            })
            .catch(() => {
                setCloudData([]);
            })
            .finally(() => {
                setCloudLoading(false);
            });
    }, []);

    const currentQuery = useMemo(() => {
        return {
            locations: filter.locations.map(item => item.id),
            profileTypes: filter.integrations,
            from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
            to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
    }, [filter]);

    useEffect(() => {
        if (filterVersion > lastFetchedVersion.current && filter.locations.length && filter.integrations.length) {
            dispatch(analyticsActions.getOperationalInsights(currentQuery));
            fetchCloudData(currentQuery);
            lastFetchedVersion.current = filterVersion;
        }
    }, [filterVersion, currentQuery]);

    const positiveWords = useMemo(
        () =>
            cloudData
                .filter(k => (k.sentiments.Positive || 0) > 0)
                .sort((a, b) => (b.sentiments.Positive || 0) - (a.sentiments.Positive || 0))
                .slice(0, 20)
                .map(k => ({ text: k.keyword, value: k.sentiments.Positive || 0 })),
        [cloudData],
    );

    const negativeWords = useMemo(
        () =>
            cloudData
                .filter(k => (k.sentiments.Negative || 0) > 0)
                .sort((a, b) => (b.sentiments.Negative || 0) - (a.sentiments.Negative || 0))
                .slice(0, 20)
                .map(k => ({ text: k.keyword, value: k.sentiments.Negative || 0 })),
        [cloudData],
    );

    const getTimeframeParams = () => ({
        from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
        to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
    });

    const buildReviewsUrl = params => {
        const searchParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null) searchParams.set(key, value);
        });
        return `${RoutePath.ReviewsManage}?${searchParams.toString()}`;
    };

    const onStrengthClick = category => {
        navigate(buildReviewsUrl({ theme: category, rating: '5', ...getTimeframeParams() }));
    };

    const onIssueClick = category => {
        navigate(buildReviewsUrl({ theme: category, rating: '1,2,3', ...getTimeframeParams() }));
    };

    const onIncidentCellClick = (locationId, category) => {
        const params = { theme: category, rating: '1,2,3', ...getTimeframeParams() };
        if (locationId !== null) params.companyId = locationId;
        navigate(buildReviewsUrl(params));
    };

    const onSentimentCellClick = (locationId, category) => {
        const params = { theme: category, ...getTimeframeParams() };
        if (locationId !== null) params.companyId = locationId;
        navigate(buildReviewsUrl(params));
    };

    const onClickKeyword = keyword => navigate(buildReviewsUrl({ keyword }));

    return (
        <InsightsContainer>
            <PairedRow>
                <TopCategoriesCard
                    title={t('AnalyticsOperationalInsights.topStrengths')}
                    subtitle={t('AnalyticsOperationalInsights.topStrengthsSubtitle')}
                    data={operationalInsightsData.topStrengths}
                    loading={operationalInsightsLoading}
                    onRowClick={onStrengthClick}
                />
                <KeywordCloud
                    words={positiveWords}
                    title={t('AnalyticsKeywords.positiveKeywords')}
                    type="positive"
                    loading={cloudLoading}
                    onClickWord={onClickKeyword}
                />
            </PairedRow>
            <PairedRow>
                <TopCategoriesCard
                    title={t('AnalyticsOperationalInsights.topIssues')}
                    subtitle={t('AnalyticsOperationalInsights.topIssuesSubtitle')}
                    data={operationalInsightsData.topIssues}
                    loading={operationalInsightsLoading}
                    onRowClick={onIssueClick}
                />
                <KeywordCloud
                    words={negativeWords}
                    title={t('AnalyticsKeywords.negativeKeywords')}
                    type="negative"
                    loading={cloudLoading}
                    onClickWord={onClickKeyword}
                />
            </PairedRow>
            <CategoryMatrixTable
                type="incidents"
                categories={operationalInsightsData.categories}
                rows={operationalInsightsData.locationRows}
                loading={operationalInsightsLoading}
                onCellClick={onIncidentCellClick}
            />
            <CategoryMatrixTable
                type="sentiment"
                categories={operationalInsightsData.categories}
                rows={operationalInsightsData.locationRows}
                loading={operationalInsightsLoading}
                onCellClick={onSentimentCellClick}
            />
            <KeywordsTableCard
                currentQuery={currentQuery}
                hasFilters={filter.locations.length > 0 && filter.integrations.length > 0}
                filterVersion={filterVersion}
                onClickKeyword={onClickKeyword}
            />
        </InsightsContainer>
    );
});

OperationalInsights.displayName = 'OperationalInsights';

export default OperationalInsights;
